import type { CatalogItemTypeFilter } from '../../catalog/domain-api';
import { Share } from 'react-native';
import { showError } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import type { BranchSalesByProductRow, SalesByProductSortKey } from '../domain/reportsTypes'
import type { PersonalOperatorFacts } from '../domain/reportsPersonalTypes';
import type {
  CashSummary,
  IntelligenceKpisResponse,
  SalesByCashierRow,
  SalesSnapshot,
} from '../application/reportsScreenData';
import type { ExpensesSummary, ProfitLossSummary } from '../../expenses/domain/expenseTypes';
import {
  buildReportsVentasShareLines,
  getReportsDateRangeLabel,
  type ReportsCustomRange,
  computeFullProfitLoss,
} from './reportsScreen.utils';
import type { DateRangePreset } from './reportsScreen.constants';
import {
  exportComprasCsv,
  exportPersonalRankingCsv,
  exportResumenCsv,
  exportSalesByProductCsv,
} from './reportsCsvExport';
import type { PurchasesSummaryReport } from '../domain/reportsPurchasesTypes';
import type { SupplierPerformanceRow } from '../domain/reportsSupplierPerformanceTypes';
import type { BranchTreasuryPeriodSummary } from '../domain/reportsTreasuryTypes';

export type ReportsShareActionsParams = {
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  todaySalesSnapshot: SalesSnapshot | null;
  periodSalesSnapshot: SalesSnapshot | null;
  periodRentabilidadKpis: IntelligenceKpisResponse | null;
  inventoryDashboard: { totalProducts: number; belowMin: number } | null;
  cashSummary: CashSummary | null;
  expensesSummary: ExpensesSummary | null;
  profitLoss: ProfitLossSummary | null;
  salesByProductCategory: string | null;
  salesByProductItemTypeFilter: CatalogItemTypeFilter;
  reportsVentasPinnedProductId: string | null;
  reportsVentasPinnedProductLabel: string | null;
  salesByProductSort: SalesByProductSortKey;
  salesByProductRows: BranchSalesByProductRow[];
  salesByProductPage: number;
  salesByProductPageSize: number;
  salesByProductTotalCount: number;
  salesByProductSumQuantity: number;
  salesByProductSumLineTotal: number;
  salesByProductUniformUnitOfMeasure: boolean;
  personalOperatorsForRanking: PersonalOperatorFacts[];
  salesByCashier: SalesByCashierRow[];
  currentBranchId: string | null;
  canEdit: boolean;
  canExport?: boolean;
  purchasesSummary?: PurchasesSummaryReport | null;
  supplierPerformance?: SupplierPerformanceRow[];
  treasurySummary?: BranchTreasuryPeriodSummary | null;
};

export function createReportsShareActions(params: ReportsShareActionsParams) {
  const canExport = params.canExport !== false;
  const customParam = (): ReportsCustomRange | null =>
    params.dateRangePreset === 'custom'
      ? { from: params.customRangeFrom, to: params.customRangeTo }
      : null;

  const denyExport = () => {
    showError('Sin permisos', 'Tu perfil no puede exportar informes.');
  };

  const handleShareSummary = async () => {
    if (!canExport) {
      denyExport();
      return;
    }
    const label = getReportsDateRangeLabel(params.dateRangePreset, customParam());
    const fullPl = computeFullProfitLoss(
      params.profitLoss,
      params.periodRentabilidadKpis?.rentabilidad
    );
    const salesTotal = params.periodSalesSnapshot?.salesTotal ?? 0;
    const salesCount = params.periodSalesSnapshot?.salesCount ?? 0;
    const ticketPromedio = salesCount > 0 ? salesTotal / salesCount : 0;
    const todaySales = params.todaySalesSnapshot?.salesTotal ?? 0;
    const todayCount = params.todaySalesSnapshot?.salesCount ?? 0;
    // KPIs de reposición no cargados (error/offline) → 'n/d', no margen ficticio en el CSV.
    const margenLoaded = params.periodRentabilidadKpis != null;

    try {
      await exportResumenCsv({
        periodLabel: label,
        facturacion: salesTotal,
        ventasCount: salesCount,
        ticketPromedio,
        margen: margenLoaded ? fullPl.grossMargin : null,
        margenPct: margenLoaded ? fullPl.grossMarginPct : null,
        gastos: fullPl.expensesTotal,
        gastosPct: fullPl.expensesTotal / Math.max(1, salesTotal),
        resultadoNeto: margenLoaded ? fullPl.netIncome : null,
        resultadoNetoPct: margenLoaded ? fullPl.netIncomePct : null,
        ventasHoy: todaySales,
        ventasHoyCount: todayCount,
        syncPendientes: 0,
        syncFallidos: 0,
        treasuryCobraste: params.treasurySummary?.collectionsTotal ?? null,
        treasuryPagasteRetiraste: params.treasurySummary?.outflowsTotal ?? null,
        treasurySaldo: params.treasurySummary?.treasuryBalance ?? null,
      });
    } catch (e: any) {
      if (e?.message && !e.message.includes('cancel')) presentUnexpectedFailure({ module: 'reports', op: 'share', error: e, title: 'Error', userMessage: 'No se pudo compartir el informe.' });
    }
  };

  const handleShareSalesByProduct = async () => {
    if (!canExport) {
      denyExport();
      return;
    }
    const label = getReportsDateRangeLabel(params.dateRangePreset, customParam());
    try {
      await exportSalesByProductCsv({
        periodLabel: label,
        rows: params.salesByProductRows,
        sumLineTotal: params.salesByProductSumLineTotal,
        sumQuantity: params.salesByProductSumQuantity,
        uniformUnitOfMeasure: params.salesByProductUniformUnitOfMeasure,
        page: params.salesByProductPage,
        pageSize: params.salesByProductPageSize,
        totalCount: params.salesByProductTotalCount,
      });
    } catch (e: any) {
      if (e?.message && !e.message.includes('cancel')) presentUnexpectedFailure({ module: 'reports', op: 'share', error: e, title: 'Error', userMessage: 'No se pudo compartir el informe.' });
    }
  };

  const handleShareVentasSummary = async () => {
    if (!canExport) {
      denyExport();
      return;
    }
    const label = getReportsDateRangeLabel(params.dateRangePreset, customParam());
    const r = params.periodRentabilidadKpis?.rentabilidad;
    const pl = computeFullProfitLoss(params.profitLoss, r);
    const lines = buildReportsVentasShareLines({
      periodLabel: label,
      salesTotal: params.periodSalesSnapshot?.salesTotal ?? 0,
      salesCount: params.periodSalesSnapshot?.salesCount ?? 0,
      salesFiscalTotal: params.periodSalesSnapshot?.fiscalSalesTotal ?? 0,
      salesFiscalCount: params.periodSalesSnapshot?.fiscalSalesCount ?? 0,
      salesNonFiscalTotal: params.periodSalesSnapshot?.nonFiscalSalesTotal ?? 0,
      salesNonFiscalCount: params.periodSalesSnapshot?.nonFiscalSalesCount ?? 0,
      pendingPayments: params.periodSalesSnapshot?.pendingPayments ?? 0,
      costoReposicionTotal: r?.costoReposicionTotal ?? null,
      margenReposicionTotal: r?.margenReposicionTotal ?? null,
      rentabilidadReposicionPct: r?.rentabilidadReposicionPct ?? null,
      expensesTotal: params.profitLoss?.expensesTotal ?? null,
      netIncome: pl.netIncome || null,
      grossMarginPct: pl.grossMarginPct || null,
      netIncomePct: pl.netIncomePct || null,
    });
    try {
      await Share.share({ message: lines.join('\n'), title: 'Ventas — Informes' });
    } catch (e: any) {
      if (e?.message && !e.message.includes('cancel')) presentUnexpectedFailure({ module: 'reports', op: 'share', error: e, title: 'Error', userMessage: 'No se pudo compartir el informe.' });
    }
  };

  const handleSharePersonalRanking = async () => {
    if (!canExport) {
      denyExport();
      return;
    }
    const label = getReportsDateRangeLabel(params.dateRangePreset, customParam());
    const rankingRows =
      params.personalOperatorsForRanking.length > 0
        ? params.personalOperatorsForRanking
        : params.salesByCashier;
    try {
      await exportPersonalRankingCsv({ periodLabel: label, rows: rankingRows });
    } catch (e: any) {
      if (e?.message && !e.message.includes('cancel')) presentUnexpectedFailure({ module: 'reports', op: 'share', error: e, title: 'Error', userMessage: 'No se pudo compartir el informe.' });
    }
  };

  const handleShareCompras = async () => {
    if (!canExport) {
      denyExport();
      return;
    }
    if (!params.purchasesSummary) {
      showError('Sin datos', 'Todavía no cargó el informe de compras. Reintentá en unos segundos.');
      return;
    }
    const label = getReportsDateRangeLabel(params.dateRangePreset, customParam());
    try {
      await exportComprasCsv({
        periodLabel: label,
        summary: params.purchasesSummary,
        supplierPerformance: params.supplierPerformance ?? [],
      });
    } catch (e: any) {
      if (e?.message && !e.message.includes('cancel')) presentUnexpectedFailure({ module: 'reports', op: 'share', error: e, title: 'Error', userMessage: 'No se pudo compartir el informe.' });
    }
  };

  return {
    handleShareSummary,
    handleShareSalesByProduct,
    handleShareVentasSummary,
    handleSharePersonalRanking,
    handleShareCompras,
  };
}
